import React, { useState, useEffect } from 'react';
import { Shield, Activity, Clock, User, AlertTriangle, FileText, Eye, RefreshCw, XCircle } from 'lucide-react';
import { apiClient } from '../lib/api';
import { toast } from '../lib/toast';
import { useUser } from '../store/useAuthStore';
import { ImpersonationLog } from '@bloom/shared';
import { formatDateTime as formatDateTimeUtil } from '../utils/dateUtils';

/**
 * ImpersonationDashboard Component
 * 
 * Admin-only dashboard for viewing impersonation activity.
 * 
 * Features:
 * - View all active impersonation sessions
 * - View complete audit history for current admin
 * - View all impersonation logs (system-wide for super admin)
 * - Filter and search capabilities
 * - Real-time updates
 * 
 * Security & Compliance:
 * - All data is read-only
 * - Complete audit trail visibility
 * - Shows who, what, when, where for every session
 */

interface ActiveSession {
  sessionId: string;
  adminEmail: string;
  adminName: string;
  targetEmail: string;
  targetName: string;
  targetRole: string;
  startTime: string;
  reason?: string;
}

// Session summary for the list view
interface SessionSummary {
  sessionId: string;
  adminName: string;
  adminEmail: string;
  targetName: string;
  targetEmail: string;
  targetRole: string;
  startTime: Date;
  endTime?: Date;
  finalAction: string;
  reason?: string;
  ipAddress: string;
  actionCount: number;
  logs: ImpersonationLog[];
}

export const ImpersonationDashboard: React.FC = () => {
  const user = useUser();
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);
  const [history, setHistory] = useState<ImpersonationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'active' | 'history'>('active');
  const [historyLimit, setHistoryLimit] = useState(50);
  const [terminatingSession, setTerminatingSession] = useState<string | null>(null);
  const [showTerminateConfirm, setShowTerminateConfirm] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<SessionSummary | null>(null);

  useEffect(() => {
    loadData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      refreshData();
    }, 30000);
    return () => clearInterval(interval);
  }, [historyLimit]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadActiveSessions(),
        loadHistory()
      ]);
    } catch (error) {
      console.error('Failed to load impersonation data:', error);
      toast.error('Failed to load impersonation data');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    try {
      setRefreshing(true);
      await Promise.all([
        loadActiveSessions(),
        loadHistory()
      ]);
    } catch (error) {
      console.error('Failed to refresh impersonation data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const loadActiveSessions = async () => {
    try {
      const response = await apiClient.getActiveSessions();
      if (response.success && response.data) {
        setActiveSessions(response.data);
      }
    } catch (error: any) {
      console.error('Failed to load active sessions:', error);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await apiClient.getImpersonationHistory(historyLimit);
      if (response.success && response.data) {
        setHistory(response.data);
      }
    } catch (error: any) {
      console.error('Failed to load history:', error);
    }
  };

  const handleTerminateSession = async (sessionId: string) => {
    try {
      setTerminatingSession(sessionId);
      const response = await apiClient.terminateImpersonationSession(sessionId);
      
      if (response.success) {
        toast.success(response.data?.message || 'Session terminated successfully');
        setShowTerminateConfirm(null);
        // Refresh the data
        await loadActiveSessions();
        await loadHistory();
      } else {
        toast.error(response.error || 'Failed to terminate session');
      }
    } catch (error: any) {
      console.error('Failed to terminate session:', error);
      toast.error('Failed to terminate session');
    } finally {
      setTerminatingSession(null);
    }
  };

  const formatDuration = (startTime: string) => {
    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 1000 / 60);
    
    if (diffMins < 60) {
      return `${diffMins} minutes`;
    }
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDateTime = (dateString: string) => formatDateTimeUtil(dateString);

  const getActionBadge = (action: string) => {
    const colors: Record<string, string> = {
      start: 'bg-green-100 text-green-800',
      end: 'bg-gray-100 text-gray-800',
      request: 'bg-blue-100 text-blue-800',
      expired: 'bg-yellow-100 text-yellow-800',
      terminated: 'bg-red-100 text-red-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (finalAction: string) => {
    switch (finalAction) {
      case 'end':
        return { color: 'bg-gray-100 text-gray-700 border-gray-300', label: 'Ended' };
      case 'expired':
        return { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', label: 'Expired' };
      case 'terminated':
        return { color: 'bg-red-100 text-red-700 border-red-300', label: 'Terminated' };
      case 'start':
        return { color: 'bg-green-100 text-green-700 border-green-300', label: 'Active' };
      default:
        return { color: 'bg-gray-100 text-gray-700 border-gray-300', label: finalAction };
    }
  };

  // Group history logs by session
  const groupedSessions: SessionSummary[] = React.useMemo(() => {
    const sessionMap = new Map<string, ImpersonationLog[]>();
    
    // Group logs by sessionId
    history.forEach(log => {
      const existing = sessionMap.get(log.sessionId) || [];
      existing.push(log);
      sessionMap.set(log.sessionId, existing);
    });
    
    // Convert to session summaries
    const summaries: SessionSummary[] = [];
    sessionMap.forEach((logs, sessionId) => {
      // Sort logs by createdAt
      const sortedLogs = [...logs].sort((a, b) => 
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      
      const startLog = sortedLogs.find(l => l.action === 'start') || sortedLogs[0];
      const endLog = sortedLogs.find(l => ['end', 'expired', 'terminated'].includes(l.action));
      
      summaries.push({
        sessionId,
        adminName: startLog.adminName,
        adminEmail: startLog.adminEmail,
        targetName: startLog.targetName,
        targetEmail: startLog.targetEmail,
        targetRole: startLog.targetRole,
        startTime: new Date(startLog.startTime),
        endTime: endLog ? new Date(endLog.endTime || endLog.createdAt) : undefined,
        finalAction: endLog?.action || 'start',
        reason: startLog.reason,
        ipAddress: startLog.ipAddress,
        actionCount: sortedLogs.length,
        logs: sortedLogs
      });
    });
    
    // Sort by start time descending
    return summaries.sort((a, b) => b.startTime.getTime() - a.startTime.getTime());
  }, [history]);

  const formatSessionDuration = (start: Date, end?: Date) => {
    const endTime = end || new Date();
    const diffMs = endTime.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 1000 / 60);
    
    if (diffMins < 1) return '< 1 min';
    if (diffMins < 60) return `${diffMins} min`;
    
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
        <p className="text-gray-600 mt-2">Only administrators can view this dashboard.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold text-gray-900 flex items-center">
              <Shield className="h-6 w-6 sm:h-8 sm:w-8 mr-2 sm:mr-3 text-blue-600" />
              <span className="hidden sm:inline">Impersonation Audit Dashboard</span>
              <span className="sm:hidden">Impersonation Audit</span>
            </h1>
            <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
              <span className="hidden sm:inline">Monitor and audit all impersonation sessions for security compliance</span>
              <span className="sm:hidden">Monitor impersonation sessions</span>
            </p>
          </div>
          
          <button
            onClick={refreshData}
            disabled={refreshing}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Security Notice */}
        <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-3 sm:p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-xs sm:text-sm text-yellow-700">
                <span className="font-bold">Security:</span>{' '}
                <span className="hidden sm:inline">All impersonation sessions are logged and monitored. This audit trail is immutable and visible to all administrators for compliance purposes.</span>
                <span className="sm:hidden">All sessions are logged for compliance.</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4 sm:mb-6">
        <nav className="-mb-px flex space-x-4 sm:space-x-8">
          <button
            onClick={() => setActiveTab('active')}
            className={`${
              activeTab === 'active'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-3 sm:py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <Activity className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Active Sessions</span>
            <span className="sm:hidden">Active</span>
            <span className="ml-1">({activeSessions.length})</span>
          </button>
          
          <button
            onClick={() => setActiveTab('history')}
            className={`${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-3 sm:py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <FileText className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Audit History</span>
            <span className="sm:hidden">History</span>
            <span className="ml-1">({history.length})</span>
          </button>
        </nav>
      </div>

      {/* Active Sessions Tab */}
      {activeTab === 'active' && (
        <div className="space-y-4">
          {activeSessions.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Eye className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No Active Sessions</h3>
              <p className="text-gray-600 mt-2">There are currently no active impersonation sessions.</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4">
                {activeSessions.map((session) => (
                  <div key={session.sessionId} className="bg-white shadow rounded-lg p-4 space-y-3">
                    {/* Admin & Target Info */}
                    <div className="flex items-start justify-between">
                      <div className="space-y-2 flex-1 min-w-0">
                        <div>
                          <div className="text-xs text-gray-500 uppercase font-medium">Admin</div>
                          <div className="text-sm font-medium text-gray-900 truncate">{session.adminName}</div>
                          <div className="text-xs text-gray-500 truncate">{session.adminEmail}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 uppercase font-medium">Impersonating</div>
                          <div className="text-sm font-medium text-gray-900 truncate">{session.targetName}</div>
                          <div className="text-xs text-gray-500 truncate">{session.targetEmail}</div>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                            {session.targetRole}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Duration & Reason */}
                    <div className="flex items-center justify-between text-sm border-t border-gray-100 pt-3">
                      <div className="flex items-center text-gray-600">
                        <Clock className="h-4 w-4 mr-1" />
                        <span>{formatDuration(session.startTime)}</span>
                      </div>
                      <div className="text-xs text-gray-500 truncate max-w-[150px]">
                        {session.reason || <span className="italic">No reason</span>}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="border-t border-gray-100 pt-3">
                      {showTerminateConfirm === session.sessionId ? (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-red-600 font-medium">Terminate this session?</span>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleTerminateSession(session.sessionId)}
                              disabled={terminatingSession === session.sessionId}
                              className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                            >
                              {terminatingSession === session.sessionId ? 'Ending...' : 'Yes, End'}
                            </button>
                            <button
                              onClick={() => setShowTerminateConfirm(null)}
                              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowTerminateConfirm(session.sessionId)}
                          className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Terminate Session
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block data-table-container">
                <div className="data-table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Admin</th>
                        <th>Impersonating</th>
                        <th>Duration</th>
                        <th>Reason</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeSessions.map((session) => (
                        <tr key={session.sessionId}>
                          <td className="cell-primary">
                            <div className="cell-inline cell-inline-icon">
                              <User className="h-5 w-5 text-gray-400" />
                              <div className="cell-stack">
                                <span className="cell-stack-value cell-truncate">{session.adminName}</span>
                                <span className="cell-stack-label cell-truncate">{session.adminEmail}</span>
                              </div>
                            </div>
                          </td>
                          <td className="cell-primary">
                            <div className="cell-stack">
                              <span className="cell-stack-value cell-truncate">{session.targetName}</span>
                              <span className="cell-stack-label cell-truncate">{session.targetEmail}</span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                                {session.targetRole}
                              </span>
                            </div>
                          </td>
                          <td className="cell-secondary">
                            <div className="cell-stack">
                              <span className="cell-stack-value cell-inline cell-inline-icon">
                                <Clock className="h-4 w-4 text-gray-400" />
                                {formatDuration(session.startTime)}
                              </span>
                              <span className="cell-stack-label">{formatDateTime(session.startTime)}</span>
                            </div>
                          </td>
                          <td className="cell-secondary">
                            <span className="cell-truncate max-w-[200px]">
                              {session.reason || <span className="cell-muted italic">No reason</span>}
                            </span>
                          </td>
                          <td className="cell-actions">
                            {showTerminateConfirm === session.sessionId ? (
                              <div className="table-actions">
                                <span className="text-xs text-red-600 font-medium">Confirm?</span>
                                <button
                                  onClick={() => handleTerminateSession(session.sessionId)}
                                  disabled={terminatingSession === session.sessionId}
                                  className="table-action-btn table-action-btn-danger"
                                >
                                  {terminatingSession === session.sessionId ? (
                                    <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full" />
                                  ) : (
                                    'Yes'
                                  )}
                                </button>
                                <button
                                  onClick={() => setShowTerminateConfirm(null)}
                                  className="table-action-btn table-action-btn-secondary"
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <div className="table-actions">
                                <button
                                  onClick={() => setShowTerminateConfirm(session.sessionId)}
                                  className="table-action-btn table-action-btn-danger"
                                  title="Terminate this session"
                                >
                                  <XCircle className="h-4 w-4" />
                                  End
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          {/* History Controls */}
          <div className="bg-white shadow sm:rounded-lg p-3 sm:p-4 mb-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Show last:
              </label>
              <select
                value={historyLimit}
                onChange={(e) => setHistoryLimit(Number(e.target.value))}
                className="ml-2 block w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value={25}>25 entries</option>
                <option value={50}>50 entries</option>
                <option value={100}>100 entries</option>
                <option value={200}>200 entries</option>
              </select>
            </div>
          </div>

          {groupedSessions.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No History</h3>
              <p className="text-gray-600 mt-2">No impersonation sessions recorded yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {groupedSessions.map((session) => {
                const statusBadge = getStatusBadge(session.finalAction);
                return (
                  <button
                    key={session.sessionId}
                    onClick={() => setSelectedSession(session)}
                    className="w-full text-left bg-white shadow rounded-lg p-4 hover:shadow-md hover:bg-gray-50 transition-all border border-transparent hover:border-blue-200"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">{session.adminName}</span>
                          <span className="text-gray-400">→</span>
                          <span className="text-sm text-gray-700 truncate">{session.targetName}</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusBadge.color} border`}>
                            {statusBadge.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>{formatDateTime(session.startTime.toString())}</span>
                          <span className="flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatSessionDuration(session.startTime, session.endTime)}
                          </span>
                          <span className="hidden sm:inline">{session.actionCount} actions</span>
                        </div>
                      </div>
                      <Eye className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Session Detail Modal */}
      {selectedSession && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedSession(null)} />
            
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-auto overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gray-50 px-4 sm:px-6 py-4 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Session Details</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {formatDateTime(selectedSession.startTime.toString())}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedSession(null)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              {/* Session Info */}
              <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 uppercase font-medium mb-1">Admin</div>
                    <div className="text-sm font-medium text-gray-900">{selectedSession.adminName}</div>
                    <div className="text-xs text-gray-500">{selectedSession.adminEmail}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase font-medium mb-1">Target User</div>
                    <div className="text-sm font-medium text-gray-900">{selectedSession.targetName}</div>
                    <div className="text-xs text-gray-500">{selectedSession.targetEmail}</div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                      {selectedSession.targetRole}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
                  <div>
                    <div className="text-xs text-gray-500 uppercase font-medium mb-1">Duration</div>
                    <div className="text-sm text-gray-900">
                      {formatSessionDuration(selectedSession.startTime, selectedSession.endTime)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase font-medium mb-1">Status</div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(selectedSession.finalAction).color} border`}>
                      {getStatusBadge(selectedSession.finalAction).label}
                    </span>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase font-medium mb-1">IP Address</div>
                    <div className="text-xs text-gray-900 font-mono">{selectedSession.ipAddress}</div>
                  </div>
                </div>
                
                {selectedSession.reason && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="text-xs text-gray-500 uppercase font-medium mb-1">Reason</div>
                    <div className="text-sm text-gray-900">{selectedSession.reason}</div>
                  </div>
                )}
              </div>
              
              {/* Action Timeline */}
              <div className="px-4 sm:px-6 py-4 max-h-80 overflow-y-auto">
                <div className="text-xs text-gray-500 uppercase font-medium mb-3">
                  Action Timeline ({selectedSession.logs.length} actions)
                </div>
                <div className="space-y-2">
                  {selectedSession.logs.map((log, index) => {
                    const isStart = log.action === 'start';
                    const isEnd = ['end', 'expired', 'terminated'].includes(log.action);
                    const isHighlighted = isStart || isEnd;
                    
                    return (
                      <div
                        key={log._id || index}
                        className={`flex items-center gap-3 p-2 rounded-lg ${
                          isHighlighted 
                            ? isStart 
                              ? 'bg-green-50 border border-green-200' 
                              : log.action === 'terminated'
                                ? 'bg-red-50 border border-red-200'
                                : log.action === 'expired'
                                  ? 'bg-yellow-50 border border-yellow-200'
                                  : 'bg-gray-50 border border-gray-200'
                            : 'bg-gray-50'
                        }`}
                      >
                        <div className="text-xs text-gray-500 font-mono w-20 flex-shrink-0">
                          {formatTime(new Date(log.createdAt))}
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getActionBadge(log.action)}`}>
                          {log.action.toUpperCase()}
                        </span>
                        {log.endpoint && (
                          <span className="text-xs text-gray-500 truncate flex-1">
                            <span className="font-medium">{log.method}</span> {log.endpoint}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="bg-gray-50 px-4 sm:px-6 py-3 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setSelectedSession(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

